<script setup lang="ts">
import { t } from '../i18n';
import { CANVAS_BACKGROUNDS as BG, settings } from '../platform';
import Toggle from './Toggle.vue';

/** Compact canvas settings popover (§6.8): background and grid (§4.4). */
const emit = defineEmits<{ close: [] }>();
</script>

<template>
  <div class="backdrop" @pointerdown="emit('close')"></div>
  <div class="float card pop glass" role="dialog" :aria-label="t('props.canvas')" @keydown.esc="emit('close')">
    <div class="row">
      <span class="lbl">{{ t('settings.backgroundColor') }}</span>
      <button v-for="c in BG" :key="c" class="swatch-btn" :class="{ active: settings.background === c }" :style="{ background: c }" :aria-label="c" @click="settings.background = c"></button>
    </div>
    <div class="row">
      <span class="lbl">{{ t('grid.title') }}</span>
      <div class="seg-pills" role="group" :aria-label="t('grid.kind')">
        <button :class="{ active: !settings.editor.grid.enabled }" @click="settings.editor.grid.enabled = false">{{ t('props.none') }}</button>
        <button v-for="k in ['dots', 'lines', 'cells'] as const" :key="k" :class="{ active: settings.editor.grid.enabled && settings.editor.grid.kind === k }" @click="(settings.editor.grid.enabled = true), (settings.editor.grid.kind = k)">
          {{ t('grid.' + k) }}
        </button>
      </div>
    </div>
    <template v-if="settings.editor.grid.enabled">
      <div class="row">
        <span class="lbl">{{ t('grid.step') }}</span>
        <input v-model.number="settings.editor.grid.step" type="number" min="2" max="1000" class="num-pill" :aria-label="t('grid.step')" />
        <input v-model="settings.editor.grid.color" type="color" class="swatch-btn" :aria-label="t('grid.color')" />
      </div>
      <div class="row">
        <span class="lbl">{{ t('grid.snap') }}</span>
        <Toggle v-model="settings.editor.snapToGrid" :label="t('grid.snap')" />
      </div>
    </template>
    <div class="row">
      <span class="lbl">{{ t('settings.smartGuides') }}</span>
      <Toggle v-model="settings.editor.smartGuides" :label="t('settings.smartGuides')" />
    </div>
    <div class="row">
      <span class="lbl">{{ t('settings.minimap') }}</span>
      <Toggle v-model="settings.minimap" :label="t('settings.minimap')" />
    </div>
    <div class="row">
      <span class="lbl">{{ t('settings.glass') }}</span>
      <input v-model.number="settings.glass" class="glass-range" type="range" min="0" max="100" step="1" :disabled="settings.noMotion" :aria-label="t('settings.glass')" />
    </div>
  </div>
</template>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 19;
}
.pop {
  top: 64px;
  /* clear of the chat button: 20px edge + 42px button + 10px gap */
  right: 72px;
  width: 340px;
  padding: 10px 14px;
  z-index: 21;
}
@media (max-width: 760px) {
  .pop {
    top: 112px;
  }
}
.glass-range {
  width: 112px;
  flex: none;
}
input[type='color'].swatch-btn {
  -webkit-appearance: none;
  appearance: none;
  overflow: hidden;
}
input[type='color'].swatch-btn::-webkit-color-swatch-wrapper {
  padding: 0;
}
input[type='color'].swatch-btn::-webkit-color-swatch {
  border: none;
  border-radius: 999px;
}
</style>
